import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { GitBranch, Plus, Workflow } from 'lucide-react';
import {
  Button,
  EmptyState,
  HelpAnchor,
  Modal,
  Pill,
  SurfacePageBody,
  SurfacePageCard,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageSectionHeader,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_PRIMARY, GLASS_INPUT } from '@/shared/ui-class';
import { AdminRowMenu } from '@/versions/v1/modules/admin/AdminRowMenu';
import { InvestigationWorkflowCatalogue } from '@/versions/v1/modules/advisory/InvestigationWorkflowCatalogue';
import { WorkflowCatalogue } from './WorkflowCatalogue';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import {
  projectModulesSeed,
  type WorkflowRun,
  type WorkflowTrigger,
} from '@/versions/v1/shared/project-modules-seed';
import {
  foundationWorkflowsLead,
  formatLastRun,
  formatTrigger,
  nextWorkflowId,
  sortWorkflows,
  statusLabel,
  statusPillTone,
} from './ProjectWorkflowsScene.transform';

export function ProjectWorkflowsScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const seed = projectModulesSeed(projectId);

  useEffect(() => {
    document.title = project ? `Workflows · ${project.name}` : 'Workflows · OptiFlow';
  }, [project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Project not found" />
          <Link to="/projects" className="text-sm text-accent-600">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  if (seed.workflowCatalogue === 'optiflow') {
    return (
      <SurfacePageLayout width="full">
        <Commentable name="ProjectWorkflowsScene">
          <SurfacePageBody width="full" className="space-y-3">
            <SurfacePageHeader
              title="Workflows"
              titleSize="large"
              actions={<HelpAnchor doc="workspace/project-workflows" label="Workflows" />}
            />
            <WorkflowCatalogue projectId={project.slug} />
          </SurfacePageBody>
        </Commentable>
      </SurfacePageLayout>
    );
  }

  return <FoundationWorkflows projectId={project.slug} projectName={project.name} />;
}

/** Foundation parity page (parity.P6d) for the non-plant demo projects. */
function FoundationWorkflows({ projectId, projectName }: { projectId: string; projectName: string }) {
  const seed = projectModulesSeed(projectId);
  const now = useMemo(() => Date.now(), []);

  const [workflows, setWorkflows] = useState<WorkflowRun[]>(() => seed.workflows);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTrigger, setNewTrigger] = useState<WorkflowTrigger>('manual');
  const runTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    setWorkflows(seed.workflows);
  }, [projectId]);

  useEffect(() => {
    const timers = runTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const visible = useMemo(() => sortWorkflows(workflows), [workflows]);

  const handleRunNow = (id: string) => {
    setWorkflows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              status: 'running',
              lastRunAt: Date.now(),
              runsCount: row.runsCount + 1,
            }
          : row,
      ),
    );

    const existing = runTimers.current.get(id);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      setWorkflows((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, status: 'success' } : row,
        ),
      );
      runTimers.current.delete(id);
    }, 1800);
    runTimers.current.set(id, timer);
  };

  const handleDisable = (id: string) => {
    const existing = runTimers.current.get(id);
    if (existing) {
      clearTimeout(existing);
      runTimers.current.delete(id);
    }
    setWorkflows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, status: 'disabled' } : row,
      ),
    );
  };

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    setWorkflows((prev) => [
      {
        id: nextWorkflowId(prev),
        name,
        trigger: newTrigger,
        lastRunAt: Date.now(),
        status: 'success',
        runsCount: 0,
      },
      ...prev,
    ]);
    setNewName('');
    setNewTrigger('manual');
    setShowNewModal(false);
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectWorkflowsScene">
        <SurfacePageBody width="full" className="space-y-6">
          <SurfacePageHeader
            kicker={projectName}
            title="Workflows"
            description="Automations and multi-step agent flows for this project."
            actions={<HelpAnchor doc="workspace/project-workflows" label="Workflows" />}
          />

          <SurfacePageSectionHeader
            title="Product workflows"
            meta="Seated in Advisory"
          />

          <InvestigationWorkflowCatalogue projectId={projectId} />

          <SurfacePageSectionHeader
            title="Workflow runs"
            meta={`${foundationWorkflowsLead(workflows).total} total`}
            actions={
              <Button
                leftIcon={<Plus size={14} aria-hidden />}
                onClick={() => setShowNewModal(true)}
              >
                New workflow
              </Button>
            }
          />

          {workflows.length === 0 ? (
            <EmptyState
              icon={<Workflow className="mx-auto h-8 w-8 text-zinc-400" />}
              title="No workflows yet"
              description="Create an automation to run on a schedule or trigger it manually when you need it."
              action={
                <button
                  type="button"
                  className={cn(BUTTON_PRIMARY, 'inline-flex h-9 items-center gap-1.5 px-4 text-sm')}
                  onClick={() => setShowNewModal(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  New workflow
                </button>
              }
            />
          ) : (
            <SurfacePageCard className="overflow-hidden p-0">
              <Table wrap={false} className="min-w-[48rem] text-left">
                <THead className="border-b border-[var(--glass-border)] bg-transparent">
                  <TR>
                    <TH className="px-5 py-3">Name</TH>
                    <TH>Trigger</TH>
                    <TH>Last run</TH>
                    <TH>Status</TH>
                    <TH>Runs</TH>
                    <TH className="w-12" />
                  </TR>
                </THead>
                <TBody className="divide-y divide-[var(--glass-border)]">
                  {visible.map((row) => (
                    <TR key={row.id} className="border-0">
                      <TD className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <GitBranch className="h-4 w-4 flex-shrink-0 text-zinc-400" />
                          <span className="font-medium text-zinc-900 dark:text-zinc-100">
                            {row.name}
                          </span>
                        </div>
                      </TD>
                      <TD className="py-3.5 text-zinc-700 dark:text-zinc-300">
                        {formatTrigger(row.trigger)}
                      </TD>
                      <TD className="py-3.5 text-muted">
                        {formatLastRun(row.lastRunAt, now)}
                      </TD>
                      <TD className="py-3.5">
                        <Pill tone={statusPillTone(row.status)}>
                          {statusLabel(row.status)}
                        </Pill>
                      </TD>
                      <TD className="py-3.5 text-zinc-700 dark:text-zinc-300">
                        {row.runsCount}
                      </TD>
                      <TD className="py-3.5 pr-4">
                        <AdminRowMenu
                          label={`Actions for ${row.name}`}
                          items={[
                            {
                              label: 'Run now',
                              disabled: row.status === 'disabled' || row.status === 'running',
                              onSelect: () => handleRunNow(row.id),
                            },
                            {
                              label: 'Disable',
                              disabled: row.status === 'disabled',
                              onSelect: () => handleDisable(row.id),
                            },
                          ]}
                        />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </SurfacePageCard>
          )}
        </SurfacePageBody>

        <Modal open={showNewModal} onOpenChange={setShowNewModal} title="New workflow">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="workflow-name" className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Name
              </label>
              <input
                id="workflow-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Weekly digest"
                className={cn('h-10 w-full rounded-lg px-3 text-sm', GLASS_INPUT)}
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Trigger
              </span>
              <div className="flex gap-2">
                {(['manual', 'schedule'] as WorkflowTrigger[]).map((trigger) => (
                  <button
                    key={trigger}
                    type="button"
                    className={cn(
                      'h-9 rounded-lg border px-3 text-sm transition-colors',
                      newTrigger === trigger
                        ? 'border-accent-500 bg-[color-mix(in_srgb,var(--color-accent-500)_10%,transparent)] text-accent-700 dark:text-accent-300'
                        : 'border-[var(--glass-border)] text-zinc-600 hover:bg-zinc-900/[0.04] dark:text-zinc-400 dark:hover:bg-white/[0.04]',
                    )}
                    onClick={() => setNewTrigger(trigger)}
                  >
                    {formatTrigger(trigger)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn-ghost inline-flex h-9 items-center px-3 text-sm"
                onClick={() => setShowNewModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={cn(BUTTON_PRIMARY, 'inline-flex h-9 items-center px-4 text-sm')}
                disabled={!newName.trim()}
                onClick={handleCreate}
              >
                Create workflow
              </button>
            </div>
          </div>
        </Modal>
      </Commentable>
    </SurfacePageLayout>
  );
}
